import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { comics, categories } from '@/mocks/comics';

const categoryMeta: Record<string, { title: string; desc: string; keywords: string }> = {
  hanman: {
    title: '韩漫在线免费观看',
    desc: '歪歪漫画网提供最新最全的韩漫在线免费观看与阅读服务，海量好看韩漫全集，支持下拉式漫画阅读模式，随时随地免费看韩漫。',
    keywords: '韩漫在线免费观看,好看的韩漫,韩漫免费看,歪歪漫画韩漫,下拉式韩漫',
  },
  riman: {
    title: '日漫在线免费阅读',
    desc: '歪歪漫画网提供精选日漫在线免费阅读服务，各种题材好看的日漫全集，支持下拉式阅读模式，随时随地免费看日漫。',
    keywords: '日漫在线免费阅读,好看的日漫,日漫免费看,歪歪漫画日漫,日漫在线',
  },
  guoman: {
    title: '国漫免费大全',
    desc: '歪歪漫画网提供优质国漫免费大全，汇集各类好看国漫全集，支持下拉式漫画阅读模式，随时随地免费看国漫。',
    keywords: '国漫免费大全,好看的国漫,国漫免费看,歪歪漫画国漫,国漫在线',
  },
  scroll: {
    title: '下拉式漫画',
    desc: '歪歪漫画网提供下拉式漫画阅读体验，海量漫画支持流畅下拉式模式，随时随地免费看韩漫日漫国漫全集。',
    keywords: '下拉式漫画,歪歪漫画下拉式,下拉式免费漫画,漫画下拉式阅读',
  },
  all: {
    title: '全部漫画',
    desc: '歪歪漫画网提供歪歪漫画在线免费观看与在线阅读服务，囊括全部海量好看漫画全集，支持下拉式模式随时随地免费看。',
    keywords: '歪歪漫画在线免费观看,歪歪漫画在线免费阅读,免费漫画全集,全部漫画',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';
  const meta = categoryMeta[currentSlug] || categoryMeta.all;
  const currentCategory = categories.find((c) => c.slug === currentSlug);

  const filteredComics = useMemo(() => {
    if (currentSlug === 'all' || currentSlug === 'scroll') return comics;
    return comics.filter((c) => c.categorySlug === currentSlug);
  }, [currentSlug]);

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `歪歪漫画网 - ${meta.title}`,
          description: meta.desc,
          url: `https://www.waiwaimanhua.com/category/${currentSlug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '歪歪漫画网',
            url: 'https://www.waiwaimanhua.com',
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.waiwaimanhua.com' },
            { '@type': 'ListItem', position: 2, name: meta.title },
          ],
        }}
      />

      <Navbar />

      <main className="pt-20">
        <section className="bg-background-100 py-10">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: meta.title },
              ]}
            />
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-3">
              {meta.title}
            </h1>
            <p className="text-sm text-foreground-500 mt-2 max-w-2xl">{meta.desc}</p>
          </div>
        </section>

        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <div className="flex items-center gap-2 mb-8 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={cat.slug === 'all' ? '/' : `/category/${cat.slug}`}
                className={`px-4 py-2 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                  currentSlug === cat.slug
                    ? 'bg-primary-500 text-background-50 font-medium'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredComics.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">暂无漫画内容</p>
              <Link
                to="/"
                className="mt-3 inline-block text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}